import { FC, useContext, useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import FlexBox from "lib/components/common/flex-box";
import SimpleCard from "lib/components/common/simple-card";
import SimpleDataTable from "lib/components/common/simple-data-table";
import SimpleModal from "lib/components/common/simple-modal";
import usePatientBookings from "lib/hooks/usePatientBookings";
import ErrorBox from "lib/components/common/error-box";
import { useNavigate } from "react-router-dom";
import usePackages from "lib/hooks/usePackages";
import useMembershipPackages from "lib/hooks/useMembershipPackages";
import { AppContext } from "lib/contexts/app-context";
import useApi from "lib/hooks/useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS } from "lib/constants/table-columns";
import PatientMembershipPackageSummaryTile from "lib/components/common/patient/patient-membership-package-summary-tile";
import { DEFAULT_PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS } from "lib/constants/default-values";
import { toast } from "sonner";


interface PatientBookAppointmentProps {}

const PatientMembershipPackage: FC<PatientBookAppointmentProps> = ({}) => {
  const [membershipPackages, setMembershipPackages] = useState<MembershipPackage[]>([]);
  const [totalCredits, setTotalCredits] = useState<number>(0);
  const [buyMembershipPackagesList, setBuyMembershipPackagesList] = useState<BuyMembershipPackageList[]>([]);
  const { error, loading } = usePatientBookings();
  const { getPatientCredits } = usePackages();
  const { getMembershipPackages, getBuyMembershipPackageList, changePatientMembershipPackageStatus } = useMembershipPackages();
  const { appState } = useContext(AppContext);
  const { postData } = useApi<CheckoutSessionResponse>();

  const navigateTo = useNavigate();

  const [showMembershipPackageDetails, setShowMembershipPackageDetails] = useState<boolean>(false);
  const [selectedMembershipPackageDetails, setSelectedMembershipPackageDetails] =
    useState<BuyMembershipPackageList>(
      DEFAULT_PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS
    );

  useEffect(() => {
    getMembershipPackages((list) => setMembershipPackages(list));
    getPatientCredits(appState.user_id, (res: any) => {
      setTotalCredits(Number(res.totalCreditsRemaining));
    });
    getBuyMembershipPackageList(appState.user_id, (list) => {
      setBuyMembershipPackagesList(list);
    });
  }, []);

  const handleBuyPackage = (membershipPackageId: string) => {
    postData(
      API_ROUTES.MEMBERSHIPPACKAGES.buy.replace(":membershipPackageId", membershipPackageId),
      {
        patientId: appState.user_id,
      },
      (response) => {
        console.log("package buy => response", response);
        window.location.href = response.url;
      }
    );
  };

  // Handler to update session booking status using API
  const onChangePatientMembershipPackageStatus = async (patientMembershipPackageId: string, status: string) => {
    try {
      await changePatientMembershipPackageStatus(appState.user_id, patientMembershipPackageId, status);
      setSelectedMembershipPackageDetails((prev) =>
        prev.id === patientMembershipPackageId ? { ...prev, status: status } : prev
      );
      getBuyMembershipPackageList(appState.user_id, (list) => {
        setBuyMembershipPackagesList(list);
      });
      toast.success(`Session status updated to '${status}'.`);
    } catch (error) {
      toast.error('Failed to update session status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading booking options...</p>
      </div>
    );
  }

  if (error) return <ErrorBox message={error} />;

  return (
    <Row>
      <Col xs={12} lg={12}>
        <SimpleCard
          title={`Your credit balance: ${totalCredits}`}
          className="shadow-sm"
        >
          <FlexBox
            direction="row"
            gap="3"
            justifyContent="left"
            className="flex-wrap justify-content-center justify-content-lg-start"
          >
            {membershipPackages.map((p) => (
              <SimpleCard key={p.id} className="membership-package-card mb-0 mb-md-3">
                <FlexBox
                  direction="column"
                  alignItems="center"
                  justifyContent="between"
                  className="h-100 p-0 p-md-4"
                >
                  <div className="w-100 text-center">
                    <h5 className="fw-bold text-primary">{p.credits} Credits</h5>
                    <h2 className="text-success fw-bold mb-2">£{p.price}</h2>
                    <p className="small text-muted mb-2">
                      {p.discount}% discount
                    </p>
                    <h6 className="text-primary fw-bold mb-2">{p.name}</h6>
                    <span className="badge bg-primary-subtle text-primary mb-3">
                      £{p.pricePerCredit} per credit
                    </span>
                  </div>
                  <Button className="mb-2" onClick={() => handleBuyPackage(p.id)}>
                    Buy Membership
                  </Button>
                  <a
                    href="https://www.heal-wellness.co.uk/terms-and-conditions/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="badge text-primary"
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Terms &amp; Conditions
                  </a>
                </FlexBox>
              </SimpleCard>
            ))}
          </FlexBox>
        </SimpleCard>
      </Col>

      <Col xs={12} lg={12} className="mt-4 mb-4">
        <SimpleCard
          title="Subscribe membership packages list"
          className="shadow-sm"
        >
            <SimpleDataTable
                  data={buyMembershipPackagesList}
                  columns={PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS}
                  isLoading={loading}
                  defaultSortFieldId="date"
                  defaultSortAsc={true}
                  onRowClick={(row: BuyMembershipPackageList) => {
                  setSelectedMembershipPackageDetails(row);
                  setShowMembershipPackageDetails(true);
                }}
                />
        </SimpleCard>
      </Col>

      <SimpleModal
        open={showMembershipPackageDetails}
        close={() => setShowMembershipPackageDetails(false)}
        title="Membership Details"
        size="sm"
      >
        <PatientMembershipPackageSummaryTile
          {...selectedMembershipPackageDetails}
          onChangePatientMembershipPackageStatus={onChangePatientMembershipPackageStatus}
        />
      </SimpleModal>
    </Row>
  );
};

export default PatientMembershipPackage;
